// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the AYNI chatbot's pill and panel, bottom left (owner decision 4,
 * `docs/AYNI_CHATBOT_XR_BRIEF.md`), mounted by `main.ts`. The site's chat
 * assistant since 2026-10-03, when it replaced TerraViz's.
 *
 * Replies are shown as plain text. Who may chat is checked before a question
 * is sent (a paid plan, `chatbot` in `src/services/xrPlans.ts`), and again
 * by the server.
 *
 * AYNI (2026-10-27, AYNI 3D's docs/AI_CHATBOT_VOICE.md): the mic talks with
 * the chatbot (`ayniChatVoice.ts`): hands-free, the replies read aloud. The
 * same voice serves the headset's dock (`voice` on the handle).
 */
import type { AiChatErrorCode, AiChatEvent } from '../ayni3d/shared/ai-chat-protocol'
import { MAX_AI_CHAT_TEXT_LENGTH } from '../ayni3d/shared/ai-chat-protocol'
import type { AiChatLanguage } from '../ayni3d/shared/ai-languages'
import type { AyniChatSession } from '../ayni-chat/ayniChat'
import { createAyniChatVoice, type AyniChatVoice } from '../ayni-chat/ayniChatVoice'
import type { VoiceEndReason, VoicePhase } from '../ayni3d/renderer/ui/ai-voice-mode'
import { t } from '../i18n'

/** Who may chat right now. */
export type AyniChatAccess = 'ok' | 'signed_out' | 'plan_required'

export interface AyniChatPanelOptions {
  session: AyniChatSession
  language(): AiChatLanguage
  access(): AyniChatAccess
  onSignIn(trigger: HTMLElement): void
  onPlans(trigger: HTMLElement): void
  /** Called when the panel opens, so other bottom-left panels can make room. */
  onOpen?(): void
  /** The parent to mount into; the body by default. */
  parent?: HTMLElement
  /** For tests: the voice's requests. */
  fetchImpl?: typeof fetch
}

export interface AyniChatPanelHandle {
  open(question?: string): void
  close(): void
  showTrigger(): void
  hideTrigger(): void
  isOpen(): boolean
  /** Re-check who may chat, after a sign-in or a licence change. */
  refreshAccess(): void
  /** Talking with the chatbot: the panel's mic and the headset's dock. */
  readonly voice: AyniChatVoice
  destroy(): void
}

/** The visitor-facing words for an engine error. */
export function errorText(code: AiChatErrorCode, message: string): string {
  switch (code) {
    case 'AI_QUOTA': return t('ayniChat.error.quota')
    case 'AI_TIMEOUT': return t('ayniChat.error.timeout')
    case 'AI_BUSY': return t('ayniChat.error.busy')
    case 'AI_RATE_LIMITED': return t('ayniChat.error.rateLimited')
    case 'AI_CANCELLED': return t('ayniChat.stopped')
    // The route answers 401 when the session has ended.
    case 'AI_NOT_CONFIGURED': return t('ayniChat.error.signIn')
    default:
      // 402: the account's plan does not include the chatbot.
      return /\b402\b/.test(message) ? t('ayniChat.gate.plan') : t('ayniChat.error.unavailable')
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

export function mountAyniChatPanel(options: AyniChatPanelOptions): AyniChatPanelHandle {
  const parent = options.parent ?? document.body

  const trigger = el('button', 'ayni-chat-trigger', t('ayniChat.trigger'))
  trigger.id = 'ayni-chat-trigger'
  trigger.type = 'button'
  trigger.setAttribute('aria-expanded', 'false')
  trigger.setAttribute('aria-controls', 'ayni-chat-panel')

  const panel = el('section', 'ayni-chat-panel hidden')
  panel.id = 'ayni-chat-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-label', t('ayniChat.title'))

  const header = el('div', 'ayni-chat-header')
  const title = el('h2', 'ayni-chat-title', t('ayniChat.title'))
  const fresh = el('button', 'ayni-chat-new', t('ayniChat.new'))
  fresh.type = 'button'
  const close = el('button', 'ayni-chat-close', '×') // i18n-exempt: a symbol; its label is the aria-label below
  close.type = 'button'
  close.setAttribute('aria-label', t('ayniChat.close'))
  header.append(title, fresh, close)

  const log = el('div', 'ayni-chat-log')
  log.setAttribute('role', 'log')
  log.setAttribute('aria-live', 'polite')

  const gate = el('div', 'ayni-chat-gate hidden')
  const gateText = el('p', 'ayni-chat-gate-text')
  const gateButton = el('button', 'ayni-chat-gate-button')
  gateButton.type = 'button'
  gate.append(gateText, gateButton)

  const form = el('form', 'ayni-chat-form')
  const input = el('textarea', 'ayni-chat-input')
  input.rows = 2
  input.maxLength = MAX_AI_CHAT_TEXT_LENGTH
  input.placeholder = t('ayniChat.input.placeholder')
  input.setAttribute('aria-label', t('ayniChat.input.label'))
  const send = el('button', 'ayni-chat-send', t('ayniChat.send'))
  send.type = 'submit'
  // AYNI: the mic, shown where the browser can record (a secure page with a microphone).
  const mic = el('button', 'ayni-chat-mic hidden', '🎤') // i18n-exempt: an icon; its label is the aria-label
  mic.type = 'button'
  mic.setAttribute('aria-pressed', 'false')
  mic.setAttribute('aria-label', t('ayniChat.mic.start'))
  mic.title = t('ayniChat.mic.start')
  form.append(input, mic, send)
  const status = el('p', 'ayni-chat-status hidden')
  status.setAttribute('role', 'status')

  panel.append(header, log, status, gate, form)
  parent.append(trigger, panel)

  let busy = false
  let open = false
  // Hidden in a meeting's audience and in embeds (main.ts): no voice there either, nor in the headset.
  let shown = true

  const addLine = (who: 'you' | 'bot' | 'note', text: string): HTMLElement => {
    const line = el('p', `ayni-chat-line ayni-chat-${who}`)
    if (who !== 'note') line.append(el('span', 'ayni-chat-who', who === 'you' ? t('ayniChat.you') : t('ayniChat.bot')))
    const body = el('span', 'ayni-chat-text', text)
    line.append(body)
    log.append(line)
    log.scrollTop = log.scrollHeight
    return body
  }

  const welcome = (): void => {
    log.replaceChildren()
    addLine('note', t('ayniChat.welcome'))
  }

  const setBusy = (next: boolean): void => {
    busy = next
    send.textContent = busy ? t('ayniChat.stop') : t('ayniChat.send')
    send.classList.toggle('ayni-chat-stop', busy)
  }

  const refreshAccess = (): void => {
    const access = options.access()
    gate.classList.toggle('hidden', access === 'ok')
    form.classList.toggle('hidden', access !== 'ok')
    mic.classList.toggle('hidden', access !== 'ok' || !voice.supported)
    if (access !== 'ok') voice.stop()
    if (access === 'signed_out') {
      gateText.textContent = t('ayniChat.gate.signedOut')
      gateButton.textContent = t('ayniChat.gate.signIn')
    } else if (access === 'plan_required') {
      gateText.textContent = t('ayniChat.gate.plan')
      gateButton.textContent = t('ayniChat.gate.plans')
    }
  }

  /**
   * Asks one question and draws it; the reply's words, or null when it failed
   * or was stopped. AYNI (owner, 2026-11-04): a spoken question is answered in
   * the language it was spoken in (`language`); a typed one in the interface's.
   */
  const ask = async (text: string, language?: AiChatLanguage): Promise<string | null> => {
    const question = text.trim()
    if (!question || busy) return null
    if (options.access() !== 'ok') {
      refreshAccess()
      return null
    }
    input.value = ''
    addLine('you', question)
    const answer = addLine('bot', t('ayniChat.thinking'))
    answer.classList.add('ayni-chat-pending')
    let words = ''
    let failed = false
    setBusy(true)
    const emit = (event: AiChatEvent): void => {
      if (event.type === 'delta') {
        words += event.text
        answer.textContent = words
        answer.classList.remove('ayni-chat-pending')
        log.scrollTop = log.scrollHeight
      } else if (event.type === 'error') {
        failed = true
        answer.classList.remove('ayni-chat-pending')
        if (words) addLine('note', errorText(event.code, event.message))
        else answer.textContent = errorText(event.code, event.message)
        if (event.code === 'AI_NOT_CONFIGURED' || /\b402\b/.test(event.message)) refreshAccess()
      }
    }
    try {
      await options.session.send(question, language ?? options.language(), emit)
    } finally {
      setBusy(false)
      // AYNI: back to the box, unless the answer moved the focus on purpose
      // (bundle.open focuses the Bundle button for the user to press).
      const moved = document.activeElement instanceof HTMLElement && document.activeElement !== document.body && !panel.contains(document.activeElement)
      if (open && !moved && voice.phase() === 'off') input.focus()
    }
    return failed || !words.trim() ? null : words
  }

  const showStatus = (text: string | null): void => {
    status.textContent = text ?? ''
    status.classList.toggle('hidden', !text)
  }
  const PHASE_STATUS: Record<Exclude<VoicePhase, 'off'>, Parameters<typeof t>[0]> = {
    listening: 'ayniChat.voice.listening',
    transcribing: 'ayniChat.voice.transcribing',
    thinking: 'ayniChat.thinking',
    speaking: 'ayniChat.voice.speaking',
  }
  const showPhase = (phase: VoicePhase, detail?: { ended?: VoiceEndReason; code?: string }): void => {
    mic.setAttribute('aria-pressed', String(phase !== 'off'))
    mic.classList.toggle('ayni-chat-mic-on', phase !== 'off')
    const label = t(phase === 'off' ? 'ayniChat.mic.start' : 'ayniChat.mic.stop')
    mic.setAttribute('aria-label', label)
    mic.title = label
    if (phase !== 'off') showStatus(t(PHASE_STATUS[phase]))
    else if (detail?.ended === 'quiet') showStatus(t('ayniChat.voice.quiet'))
    else if (detail?.ended === 'mic-denied') showStatus(t('ayniChat.voice.micDenied'))
    else if (detail?.ended === 'error') showStatus(errorText((detail.code ?? 'AI_FAILED') as AiChatErrorCode, ''))
    else showStatus(null)
  }

  const voice = createAyniChatVoice({
    language: () => options.language(),
    allowed: () => shown && options.access() === 'ok',
    ask: (text, language) => ask(text, language),
    cancel: () => options.session.cancel(),
    onPhase: showPhase,
    ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
  })

  const doOpen = (question?: string): void => {
    if (!open) {
      open = true
      panel.classList.remove('hidden')
      trigger.setAttribute('aria-expanded', 'true')
      trigger.classList.add('ayni-chat-trigger-active')
      if (!log.childElementCount) welcome()
      options.onOpen?.()
    }
    refreshAccess()
    if (question) input.value = question.slice(0, MAX_AI_CHAT_TEXT_LENGTH)
    if (options.access() === 'ok') input.focus()
    else gateButton.focus()
  }

  const doClose = (): void => {
    if (!open) return
    open = false
    panel.classList.add('hidden')
    trigger.setAttribute('aria-expanded', 'false')
    trigger.classList.remove('ayni-chat-trigger-active')
  }

  trigger.addEventListener('click', () => (open ? doClose() : doOpen()))
  close.addEventListener('click', () => {
    doClose()
    trigger.focus()
  })
  mic.addEventListener('click', () => voice.tap())
  fresh.addEventListener('click', () => {
    voice.stop()
    options.session.reset()
    setBusy(false)
    welcome()
    input.focus()
  })
  gateButton.addEventListener('click', () => {
    if (options.access() === 'signed_out') options.onSignIn(gateButton)
    else options.onPlans(gateButton)
  })
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    if (busy) {
      voice.stop()
      options.session.cancel()
    } else {
      // Typing takes over from talking.
      voice.stop()
      void ask(input.value)
    }
  })
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      if (!busy) void ask(input.value)
    }
  })
  panel.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      doClose()
      trigger.focus()
    }
  })

  // Ride above the info panel, as the transport does, so neither covers the other.
  const infoPanel = document.getElementById('info-panel')
  let observer: ResizeObserver | null = null
  const lift = (): void => {
    const shown = infoPanel && !infoPanel.classList.contains('hidden') ? infoPanel.getBoundingClientRect().height : 0
    const value = shown > 0 ? `${Math.round(shown)}px` : '0px'
    trigger.style.setProperty('--ayni-chat-lift', value)
    panel.style.setProperty('--ayni-chat-lift', value)
    // The space events corner rides above the pill too (satellites.css).
    document.documentElement.style.setProperty('--ayni-chat-lift', value)
  }
  if (infoPanel && typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(lift)
    observer.observe(infoPanel)
    lift()
  }

  return {
    open: doOpen,
    close: doClose,
    showTrigger: () => {
      shown = true
      trigger.classList.remove('hidden')
    },
    hideTrigger: () => {
      shown = false
      voice.stop()
      trigger.classList.add('hidden')
      doClose()
    },
    isOpen: () => open,
    refreshAccess,
    voice,
    destroy: () => {
      voice.stop()
      observer?.disconnect()
      options.session.cancel()
      trigger.remove()
      panel.remove()
    },
  }
}
